import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatOpeningPeriods } from '../utils/openingHours';
import { 
  ArrowLeft, 
  Edit, 
  Store, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  AlertTriangle,
  Badge,
  Instagram,
  Facebook,
  ExternalLink,
  Users,
  User
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';

interface PractitionerAssociation {
  id: string;
  practitionerId: string;
  practitionerFirstName: string;
  practitionerLastName: string;
  practitionerEmail: string;
  relationshipType: 'FREELANCE' | 'EMPLOYEE' | 'RENTAL' | 'PARTNERSHIP';
  hourlyRate?: number;
  commissionRate?: number;
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

interface Shop {
  id: string;
  name: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  phone: string;
  email: string;
  website?: string;
  socialMedia: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
  };
  openingHours: {
    monday?: { open: string; close: string };
    tuesday?: { open: string; close: string };
    wednesday?: { open: string; close: string };
    thursday?: { open: string; close: string };
    friday?: { open: string; close: string };
    saturday?: { open: string; close: string };
    sunday?: { open: string; close: string };
  };
  services: Array<{
    serviceId: string;
    available: boolean;
    customPricing?: number;
  }>;
  facilities: Array<{
    type: string;
    description: string;
    available: boolean;
  }>;
  policies: {
    cancellation: string;
    deposit: string;
    aftercare: string;
    photography: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function ShopViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [shop, setShop] = useState<Shop | null>(null);
  const [associatedPractitioners, setAssociatedPractitioners] = useState<PractitionerAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchShop(id);
      fetchAssociatedPractitioners(id);
    }
  }, [id]);

  const fetchShop = async (shopId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/admin/shops/${shopId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch shop');
      }

      const data = await response.json();
      if (data.success) {
        const shopData = data.data.shop;
        // Ensure arrays are properly initialized
        setShop({
          ...shopData,
          services: Array.isArray(shopData.services) ? shopData.services : [],
          facilities: Array.isArray(shopData.facilities) ? shopData.facilities : [],
          openingHours: shopData.openingHours || {},
          socialMedia: shopData.socialMedia || { instagram: '', facebook: '', tiktok: '' },
          policies: shopData.policies || { cancellation: '', deposit: '', aftercare: '', photography: '' },
          address: shopData.address || { street: '', city: '', postalCode: '', country: 'France' }
        });
      } else {
        setError(data.message || 'Failed to load shop');
      }
    } catch (error) {
      console.error('Fetch shop error:', error);
      setError('Failed to load shop data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociatedPractitioners = async (shopId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/shops/${shopId}/practitioners`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAssociatedPractitioners(data.data.practitioners || []);
        }
      }
    } catch (error) {
      console.error('Fetch associated practitioners error:', error);
    }
  };

  const handleEdit = () => {
    if (shop) {
      navigate(`/shops/${shop.id}/edit`);
    }
  };

  const handleBackToList = () => {
    navigate('/shops');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const formatOpeningHours = (openingHours: any) => {
    const daysOfWeek = [
      { key: 'monday', label: 'Lundi' },
      { key: 'tuesday', label: 'Mardi' },
      { key: 'wednesday', label: 'Mercredi' },
      { key: 'thursday', label: 'Jeudi' },
      { key: 'friday', label: 'Vendredi' },
      { key: 'saturday', label: 'Samedi' },
      { key: 'sunday', label: 'Dimanche' }
    ];

    return daysOfWeek.map(day => {
      return {
        day: day.label,
        hours: formatOpeningPeriods(openingHours?.[day.key])
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du shop...</span>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Shop non trouvé'}
            </span>
          </div>
        </div>
        <button
          onClick={handleBackToList}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des shops
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>{shop.name}</span>
              <HelpIcon 
                docPath="04-boutiques#04-4-consultation" 
                tooltip="Documentation : Consultation d'une boutique"
              />
            </h1>
            <p className="text-textSecondary">Détails du shop</p>
          </div>
        </div>

        <button
          onClick={handleEdit}
          className="btn-primary flex items-center space-x-2"
        >
          <Edit className="w-4 h-4" />
          <span>Modifier</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-8">
          {/* Basic Information */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Store className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Téléphone</p>
                  <p className="font-medium">{shop.phone}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{shop.email}</p>
                </div>
              </div>

              {shop.website && (
                <div className="flex items-center space-x-3 md:col-span-2">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Site Web</p>
                    <a 
                      href={shop.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                    >
                      <span>{shop.website}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                <MapPin className="w-5 h-5 text-accent-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Adresse</h2>
            </div>

            <div className="space-y-2">
              <p className="font-medium text-textPrimary">{shop.address.street}</p>
              <p className="text-textSecondary">
                {shop.address.city}, {shop.address.postalCode}
              </p>
              <p className="text-textSecondary">{shop.address.country}</p>
            </div>
          </div>

          {/* Facilities */}
          {shop.facilities.length > 0 && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <Store className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Équipements</h2>
              </div>

              <div className="space-y-4">
                {shop.facilities.map((facility, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-textPrimary">{facility.type}</h3>
                        <p className="text-sm text-gray-600">{facility.description}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        facility.available 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {facility.available ? 'Disponible' : 'Indisponible'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Policies */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
                <Globe className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Politiques</h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-textPrimary mb-1">Annulation</h3>
                <p className="text-sm text-gray-600">{shop.policies.cancellation}</p>
              </div>
              <div>
                <h3 className="font-medium text-textPrimary mb-1">Acompte</h3>
                <p className="text-sm text-gray-600">{shop.policies.deposit}</p>
              </div>
              <div>
                <h3 className="font-medium text-textPrimary mb-1">Soins</h3>
                <p className="text-sm text-gray-600">{shop.policies.aftercare}</p>
              </div>
              <div>
                <h3 className="font-medium text-textPrimary mb-1">Photographie</h3>
                <p className="text-sm text-gray-600">{shop.policies.photography}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Schedule & Social */}
        <div className="space-y-8">
          {/* Status */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
                <Badge className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Statut</h2>
            </div>

            <div className="text-center">
              {getStatusBadge(shop.isActive)}
            </div>
          </div>

          {/* Opening Hours */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Horaires</h2>
            </div>

            <div className="space-y-3">
              {formatOpeningHours(shop.openingHours).map((schedule, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{schedule.day}</span>
                  <span className={`font-medium ${schedule.hours === 'Fermé' ? 'text-gray-400' : 'text-textPrimary'}`}>
                    {schedule.hours}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Practitioners Associations */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Artistes Associés</h2>
            </div>

            {associatedPractitioners.length > 0 ? (
              <div className="space-y-3">
                {associatedPractitioners.map((association) => (
                  <div key={association.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <h3 className="font-medium text-textPrimary">
                        {association.practitionerFirstName} {association.practitionerLastName}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          association.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {association.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {association.relationshipType === 'FREELANCE' && 'Freelance'}
                          {association.relationshipType === 'EMPLOYEE' && 'Employé'}
                          {association.relationshipType === 'RENTAL' && 'Location'}
                          {association.relationshipType === 'PARTNERSHIP' && 'Partenariat'}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{association.practitionerEmail}</p>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {association.hourlyRate && (
                        <div>
                          <span className="text-gray-500">Tarif:</span>
                          <span className="ml-1 font-medium">{association.hourlyRate}€/h</span>
                        </div>
                      )}
                      {association.commissionRate && (
                        <div>
                          <span className="text-gray-500">Commission:</span>
                          <span className="ml-1 font-medium">{association.commissionRate}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Aucun artiste associé</p>
            )}
          </div>

          {/* Social Media */}
          <div className="card">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                <Globe className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Réseaux Sociaux</h2>
            </div>

            <div className="space-y-3">
              {shop.socialMedia.instagram && (
                <div className="flex items-center space-x-3">
                  <Instagram className="w-5 h-5 text-pink-500" />
                  <a 
                    href={`https://instagram.com/${shop.socialMedia.instagram.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                  >
                    <span>{shop.socialMedia.instagram}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              
              {shop.socialMedia.facebook && (
                <div className="flex items-center space-x-3">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <a 
                    href={`https://facebook.com/${shop.socialMedia.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                  >
                    <span>{shop.socialMedia.facebook}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              
              {shop.socialMedia.tiktok && (
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-gray-800" />
                  <a 
                    href={`https://tiktok.com/${shop.socialMedia.tiktok.replace('@', '@')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                  >
                    <span>{shop.socialMedia.tiktok}</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}

              {!shop.socialMedia.instagram && !shop.socialMedia.facebook && !shop.socialMedia.tiktok && (
                <p className="text-gray-500 text-sm">Aucun réseau social configuré</p>
              )}
            </div>
          </div>

          {/* Account Info */}
          <div className="card">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Informations du compte</h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Créé le</span>
                <span className="font-medium">{formatDate(shop.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Dernière modification</span>
                <span className="font-medium">{formatDate(shop.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}